import Game from '../Game';
import Scene from '../Scene/Scene';
import { IGameState } from '../State/Game/IGameState';
import StateManager from '../State/StateManager';
import TaskQueue from '../Utility/Task/TaskQueue';

export interface ICoroutine {
    result?: IteratorResult<number>;
    iterator: Iterator<number>;
}

export default abstract class System {
    public state: StateManager;
    public scene: Scene;
    public taskQueue: TaskQueue<System>;
    public coroutines: ICoroutine[];

    private _active: boolean = false;

    constructor(state: StateManager) {
        this.state = state;
        this.taskQueue = new TaskQueue();
        this.coroutines = [];
    }

    public abstract init(): void;

    public abstract async preload(): Promise<any>;

    public abstract handleStateChanged(state: IGameState): void;

    public isActive(): boolean {
        return this._active;
    }

    public start(): void {
        this._active = true;
        this.onStart();
    }

    public stop(): void {
        this._active = false;
        this.coroutines.forEach(routine => this.stopCoroutine(routine));
        this.onStop();
    }

    public destroy(): void {
        this.onDestroy();
        this.coroutines = null;
        this.taskQueue = null;
    }

    public update(): void {
        this.onUpdate();
        this.taskQueue.update();
        this.updateCoroutines();
    }

    public lateUpdate(): void {
        this.onLateUpdate();
    }

    public startCoroutine(iterator: Iterator<number>): ICoroutine {
        const routine: ICoroutine = {
            iterator: iterator,
        };
        this.coroutines.push(routine);

        return routine;
    }

    public stopCoroutine(routine: any) {
        const index = this.coroutines.indexOf(routine);
        if (index !== -1) {
            this.coroutines.splice(index, 1);
        }
    }

    protected abstract onStart(): void;

    protected abstract onStop(): void;

    protected abstract onDestroy(): void;

    protected abstract onUpdate(): void;

    protected abstract onLateUpdate(): void;

    private updateCoroutines() {
        const deltaTime: number = Game.ticker.getDeltaTime();
        for (let i = 0; i < this.coroutines.length; i++) {
            this.coroutines[i].result = this.coroutines[i].iterator.next(
                deltaTime
            );
            if (this.coroutines[i].result.done) {
                this.coroutines.splice(i--, 1);
            }
        }
    }
}
